import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, FileSearch, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DocumentViewer } from "../components/DocumentViewer";
import { Explainer } from "../components/Explainer";
import { Mismatch, type Candidate as MismatchCandidate } from "../components/Mismatch";
import { useMe } from "../lib/me";
import { displayName, fileName, percent } from "../lib/words";
import { RoleButton } from "../components/RequireRole";
import { listNote, type ListStatus } from "../components/States";
import { TableScroll } from "../components/TableScroll";
import {
  chooseValue, conflictsOf, extractionView, loadResult, nextStep, resultExpected, resultQueryKey, retryResult,
  startVendorCheck,
} from "./extractionFlow";
import { FieldRow } from "../components/FieldRow";
import { Pill } from "../components/Pill";
import { AssetLink } from "../components/register/AssetLink";
import { Stages, stageLabel } from "../components/Stages";
import { NeioTraces } from "../components/neio/TraceList";
import {
  api, uc5, DOC_TYPE_LABELS, RESOLUTION_LABELS,
  type Field, type Job, type Result,
} from "../lib/api";

export function Extraction({
  jobs, status = "ready", job, setJob, refresh, openNew, setOpenNew, query,
}: {
  jobs: Job[]; status?: ListStatus; job: Job | null; setJob: (j: Job | null) => void;
  refresh: () => void; openNew: boolean; setOpenNew: (v: boolean) => void; query: string;
}) {
  const [local, setFilter] = useState("");
  const filter = query || local;
  const [tag, setTag] = useState("PT-403");
  const [pkg, setPkg] = useState("MSO");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cited, setCited] = useState<Field | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const { me } = useMe();
  const client = useQueryClient();
  /** The field a choice is being saved for, and any refusal, by field. */
  const [choosing, setChoosing] = useState<string | null>(null);
  const [choiceError, setChoiceError] = useState<Record<string, string>>({});

  const confirmedPaths =
    job?.selected_documents?.map((d) => d.path) ??
    (job?.selected_document ? [job.selected_document] : []);

  // Keyed on the status, and retried while the server has not stored it yet, so a read that
  // finishes while the screen is open shows its summary without a reload.
  const resultQuery = useQuery({
    queryKey: job ? resultQueryKey(job) : ["uc1-result", null],
    queryFn: () => loadResult(job!.id),
    enabled: resultExpected(job),
    retry: retryResult,
    retryDelay: 1000,
  });
  const result: Result | null = resultExpected(job) ? (resultQuery.data ?? null) : null;
  const setResult = (r: Result) => { if (job) client.setQueryData(resultQueryKey(job), r); };

  // The vendor checks, to link a finished extraction to the one already started from it.
  const navigate = useNavigate();
  const checks = useQuery({ queryKey: ["uc5-jobs"], queryFn: uc5.list, enabled: resultExpected(job) });
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  async function checkVendor() {
    if (!job) return;
    setStarting(true); setStartError(null);
    try {
      const started = await startVendorCheck(job.id);
      void client.invalidateQueries({ queryKey: ["uc5-jobs"] });
      refresh();
      navigate(`/vendor/${encodeURIComponent(started.id)}`);
    } catch (e) {
      setStartError((e as Error).message);
    } finally {
      setStarting(false);
    }
  }

  // Preselect what the backend proposed. A person can change it, and the default is the set
  // that covers the most identity fields rather than the single highest-confidence document.
  useEffect(() => {
    setSelected((job?.candidates ?? []).filter((c) => c.recommended).map((c) => c.path));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the job, not every field of it
  }, [job?.id]);

  function toggle(path: string) {
    setSelected((current) =>
      current.includes(path) ? current.filter((p) => p !== path) : [...current, path],
    );
  }

  useEffect(() => {
    if (!job || !["parsing", "extracting"].includes(job.status)) return;
    const t = setInterval(async () => {
      const fresh = await api.getJob(job.id);
      setJob(fresh);
      if (!["parsing", "extracting"].includes(fresh.status)) { clearInterval(t); refresh(); }
    }, 2000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restart polling only when the job changes
  }, [job]);

  async function start() {
    setBusy(true); setError(null); setCited(null);
    try { setJob(await api.createJob(tag.trim(), pkg)); setOpenNew(false); refresh(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function confirm(paths: string[]) {
    if (!job || paths.length === 0) return;
    setBusy(true); setError(null);
    try { setJob(await api.confirm(job.id, paths)); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  /**
   * Conflicts are between documents, not between the two readers of one page. Each candidate is
   * a real document and opens at its own page.
   */
  const conflicts = conflictsOf(result);

  async function choose(field: string, c: MismatchCandidate, note: string) {
    if (!job) return;
    setChoosing(field);
    setChoiceError((e) => ({ ...e, [field]: "" }));
    try {
      setResult(await chooseValue(job.id, field, { value: c.value, documentId: c.documentId }, note));
      // A chosen field drops out of the review queue.
      void client.invalidateQueries({ queryKey: ["reviews"] });
    } catch (e) {
      setChoiceError((errs) => ({ ...errs, [field]: (e as Error).message }));
    } finally {
      setChoosing(null);
    }
  }

  const view = job
    ? extractionView(job, { selected: selected.length, sending: busy, hasResult: !!result })
    : null;

  const shown = jobs.filter(
    (j) => !filter || j.tag.toLowerCase().includes(filter.toLowerCase()) || j.id.includes(filter),
  );

  return (
    <div className="workspace">
      <section className="pane">
        <div className="joblog-head">
          <strong style={{ fontSize: 17 }}>Job Log</strong><span className="chip">UC1</span>
        </div>
        <div style={{ marginBottom: 14 }}>
          <RoleButton role="engineer" me={me} style={{ width: "100%" }} aria-expanded={openNew}
                      onClick={() => setOpenNew(!openNew)}>
            <span className="row" style={{ justifyContent: "center" }}><Plus size={16} aria-hidden="true" /> New Job</span>
          </RoleButton>
        </div>

        {openNew && (
          <div className="card" style={{ marginBottom: 14, padding: 16 }}>
            <div className="stack" style={{ gap: 10 }}>
              <input value={tag} onChange={(e) => setTag(e.target.value)} aria-label="Equipment tag"
                     placeholder="Equipment tag, e.g. PT-403" />
              <select value={pkg} onChange={(e) => setPkg(e.target.value)} aria-label="Package">
                <option>MSO</option><option>GTG</option>
              </select>
              <RoleButton role="engineer" me={me} onClick={start} disabled={busy || !tag.trim()}>
                {busy ? "Searching..." : "Find documents"}
              </RoleButton>
            </div>
          </div>
        )}

        <div className="searchbox">
          <Search aria-hidden="true" /><input value={local} onChange={(e) => setFilter(e.target.value)}
                           aria-label="Search jobs by ID or tag" placeholder="Search jobs by ID or tag..." />
        </div>

        {(() => {
          const note = listNote(status, shown.length, jobs.length ? "No job matches." : "No jobs yet.", "Loading jobs");
          return note && <p className="muted" style={{ fontSize: 13 }}>{note}</p>;
        })()}
        {shown.map((j) => (
          <Link key={j.id} to={`/extraction/${j.id}`} className={`jobcard ${job?.id === j.id ? "selected" : ""}`}
                aria-current={job?.id === j.id ? "page" : undefined}>
            <span className="top">
              <span className="id">J-{j.id.slice(0, 4).toUpperCase()}</span><Pill value={j.status} />
            </span>
            <span className="tag">{j.tag}</span>
            <span className="sub">{stageLabel(j.stage)}</span>
          </Link>
        ))}
      </section>

      <section className="detail">
        {error && <div className="card" style={{ marginBottom: 16 }}><span className="pill fail">error</span> {error}</div>}
        {!job ? (
          <div className="empty">
            <div><FileSearch size={40} style={{ opacity: .3 }} />
              <p style={{ marginTop: 12 }}>Select a job, or start a new one.</p></div>
          </div>
        ) : (
          <div className="stack">
            <div className="detail-head">
              <h2>J-{job.id.slice(0, 4).toUpperCase()}</h2>
              <span className="tagchip">{job.tag}</span>
              <Pill value={job.status} />
              <span className="muted" style={{ fontSize: 13, marginLeft: "auto" }}>{job.package} package</span>
              <AssetLink pkg={job.package} tag={job.tag} />
            </div>

            <Stages stage={job.stage} failed={job.status === "failed"} />

            {job.status === "failed" && (
              <div className="card"><Explainer>
                Searched the {job.package} package index for {job.tag} and found no matching entry.
                Rather than widen the search until something appeared, the job stops here so an
                engineer can confirm the tag or choose a different package.
              </Explainer></div>
            )}

            {view?.offerRead && job.candidates && (
              <div className="card">
                <div className="section-title">
                  <span className="stage-chip">Stage 2</span><h3>Confirm the documents to read</h3>
                </div>
                <Explainer>
                  Found {job.candidates.length}{" "}
                  {job.candidates.length === 1 ? "document" : "documents"} that mention {job.tag}.
                  Nothing has been opened at the paid tier yet. A tag's identity is usually spread
                  across several documents, so {selected.length} of them{" "}
                  {selected.length === 1 ? "is" : "are"} proposed: the serial number comes off the
                  test certificate for that unit, the part number off the drawing that carries the
                  part list, the model off the data sheet.
                </Explainer>
                <TableScroll label="Candidate documents" style={{ marginTop: 16 }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}><span className="visually-hidden">Read</span></th>
                      <th>Document</th><th>Type</th><th>Title</th>
                      <th>Found by</th><th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.candidates.map((c) => (
                      <tr key={c.path}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(c.path)}
                            onChange={() => toggle(c.path)}
                            aria-label={`read ${displayName(c.file_name)}`}
                          />
                        </td>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {displayName(c.file_name)}
                          {c.found_on_pages.length > 0 && (
                            <span className="muted"> {c.found_on_pages.length === 1 ? "page" : "pages"} {c.found_on_pages.join(", ")}</span>
                          )}
                        </td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {DOC_TYPE_LABELS[c.doc_type ?? ""] ?? c.doc_type ?? "-"}
                        </td>
                        <td>{c.title ? displayName(c.title) : "-"}</td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {RESOLUTION_LABELS[c.resolution] ?? c.resolution.replace(/_/g, " ")}
                        </td>
                        <td className="mono">{percent(c.confidence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </TableScroll>
                <div className="row" style={{ marginTop: 16, gap: 12 }}>
                  <RoleButton role="engineer" me={me} onClick={() => confirm(selected)} disabled={view.readDisabled}>
                    {view.readLabel}
                  </RoleButton>
                  <span className="muted" style={{ fontSize: 12 }}>
                    Each document read costs one parse. Four is the most a job will read.
                  </span>
                </div>
              </div>
            )}

            {confirmedPaths.length > 0 && ["parsing", "extracting"].includes(job.status) && (
              <div className="card"><Explainer>
                Reading {confirmedPaths.map(fileName).join(", ")}. Each page is
                read twice: once for the values, once for where they sit on the page. Where two
                documents state the same value, that agreement is the strongest evidence available.
              </Explainer></div>
            )}

            {view?.summary === "loading" && (
              <div className="card" role="status" aria-live="polite">
                <div className="section-title">
                  <span className="stage-chip">Stage 5</span><h3>Extraction Summary</h3>
                </div>
                {resultQuery.isError && !retryResult(0, resultQuery.error) ? (
                  <p className="muted">The summary could not be loaded: {(resultQuery.error as Error).message}</p>
                ) : resultQuery.isError ? (
                  <p className="muted">
                    The documents were read but the summary is not ready.{" "}
                    <button className="ghost" onClick={() => void resultQuery.refetch()}>Try again</button>
                  </p>
                ) : (
                  <p className="muted">Reading done. Loading the summary...</p>
                )}
              </div>
            )}

            {result && (
              <div className="card">
                <div className="section-title">
                  <span className="stage-chip">Stage 5</span><h3>Extraction Summary</h3>
                  <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>read by {result.model}</span>
                </div>
                <Explainer>
                  Assembled the five identity fields from {result.sources.length}{" "}
                  {result.sources.length === 1 ? "document" : "documents"}, each value showing the
                  document and page it came from.{" "}
                  {(result.completeness * 5).toFixed(0)} of 5 fields answered.{" "}
                  {result.needs_review.length > 0
                    ? `${result.needs_review.length} need a person: either two documents disagree, the two readers disagreed, or the value could not be placed on the page.`
                    : "Nothing is in conflict and every value that was found could be placed on its page."}
                </Explainer>
                <TableScroll label="Extracted fields" style={{ marginTop: 16 }}>
                <table>
                  <thead><tr><th>Field</th><th>Value</th><th>Confidence</th><th>State</th><th>Source</th></tr></thead>
                  <tbody>{result.fields.map((f) => <FieldRow key={f.name} field={f} onCite={setCited} />)}</tbody>
                </table>
                </TableScroll>
                <div className="muted" style={{ fontSize: 12, marginTop: 14 }}>
                  Read from:{" "}
                  {result.sources
                    .map(
                      (s) =>
                        `${fileName(s.source_path)} (${
                          DOC_TYPE_LABELS[s.doc_type] ?? s.doc_type
                        })`,
                    )
                    .join(", ")}
                </div>
              </div>
            )}

            {result && job && (() => {
              const step = nextStep(job, checks.data ?? []);
              if (!step) return null;
              return (
                <div className="card">
                  <div className="section-title">
                    <span className="stage-chip">Next</span><h3>Vendor and obsolescence</h3>
                  </div>
                  <p className="muted" style={{ marginBottom: 12 }}>
                    {step.kind === "open"
                      ? `A vendor and obsolescence check has been started from this extraction.`
                      : `Research whether ${job.tag}'s part is still made and what replaces it, using the values above.`}
                    {conflicts.some((c) => !c.chosen) && " Choose a value for each conflicting field first, or the check will stop on it."}
                  </p>
                  {step.kind === "open" ? (
                    <Link to={`/vendor/${encodeURIComponent(step.id)}`} className="row" style={{ gap: 6 }}>
                      Open the vendor check <ArrowRight size={14} aria-hidden="true" />
                    </Link>
                  ) : (
                    <RoleButton role="engineer" me={me} onClick={() => void checkVendor()} disabled={starting}>
                      {starting ? "Starting..." : "Check vendor and obsolescence"}
                    </RoleButton>
                  )}
                  {startError && <p role="alert" style={{ marginTop: 8 }}><span className="pill fail">error</span> {startError}</p>}
                </div>
              );
            })()}

            {cited && cited.provenance.page_number && (
              /* The document the value came from, not the job's first document. A record spans
                 several, and opening the wrong one reads as a wrong extraction. */
              <DocumentViewer
                pkg={job?.package ?? null}
                path={cited.provenance.source_path ?? confirmedPaths[0]}
                page={cited.provenance.page_number}
                box={cited.provenance.box}
                label={cited.value ?? cited.name}
                onClose={() => setCited(null)}
              />
            )}

            {result && conflicts.length > 0 && (
              <div className="card">
                <div className="section-title">
                  <span className="stage-chip">Stage 4</span><h3>Resolve conflicting values</h3>
                  <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
                    {conflicts.filter((c) => c.chosen).length} of {conflicts.length} chosen
                  </span>
                </div>
                <Explainer>
                  Two documents state different values for the same field. The system does not
                  choose between them, and a preferred document type does not settle it either.
                  Each candidate is shown with the part of its own page it came from, so the
                  decision is made on the evidence.
                </Explainer>
                <div className="stack" style={{ marginTop: 16 }}>
                  {conflicts.map((c) => (
                    <Mismatch key={`${c.name}:${c.chosen?.at ?? ""}`} field={c.label} candidates={c.candidates}
                              me={me} chosen={c.chosen} busy={choosing === c.name}
                              error={choiceError[c.name] || null}
                              onResolve={(pick, note) => void choose(c.name, pick, note)} />
                  ))}
                </div>
              </div>
            )}

            <NeioTraces jobId={job.id} />
          </div>
        )}
      </section>
    </div>
  );
}
